import React, { createContext, useCallback, useContext, useState } from "react";

const SessionContext = createContext(null);

export const createEmptySessionPreferences = () => ({
  goal: null,
  experience: null,
  beard_shadow: null,
  notes: "",
  lip_finish: null,
  eye_finish: null,
  coverage: null,
  texture: null,
  skip_categories: [],
  ethics: [],
  owned_products: "",
  contour_choice: null,
});

export const SessionProvider = ({ children }) => {
  const [preferences, setPreferences] = useState({
    // First-time users see NO chip pre-selected on these pages — we
    // want the user to make a conscious choice, not nudge them
    // toward a default. Returning users with saved style_preferences
    // re-hydrate to whatever they previously picked, which is then
    // shown highlighted (their own previous choice, not our nudge).
    goal: null,
    experience: null,
    beard_shadow: null,
    notes: "",
    // Style preferences — collected in onboarding. `unsure` means the
    // coach picks for you (great for beginners). `null` = not yet asked.
    lip_finish: null,
    eye_finish: null,
    coverage: null,
    texture: null,
    skip_categories: [],
    ethics: [],
    // Free-text: products the user already owns. The coach weaves
    // these into tips instead of pushing them to buy new things.
    owned_products: "",
    // Per-routine contour choice — asked fresh on each Capture
    // session. `null` = not yet asked / will use defaults; "skip" =
    // explicitly no contour; vibe key = use that vibe. NOT persisted
    // to user prefs; soft-remembered in localStorage so the chip
    // pre-selects to the last pick.
    contour_choice: null,
  });
  const [photo, setPhoto] = useState(null); // { base64, mime, previewUrl }
  const [routine, setRoutine] = useState(null);
  // Inspiration brief from /recreate flow. When set, the next analyze
  // call adapts THIS look to the user. Cleared automatically after
  // analyze succeeds so the next session starts fresh.
  const [inspiration, setInspiration] = useState(null);

  const reset = useCallback(() => {
    setPhoto(null);
    setRoutine(null);
    setInspiration(null);
  }, []);

  // Used by the public /demo entry point. Everything in this context is
  // React memory only, so resetting here cannot touch an account, browser
  // storage, or the database.
  const resetEphemeralDemo = useCallback(() => {
    setPreferences(createEmptySessionPreferences());
    setPhoto(null);
    setRoutine(null);
    setInspiration(null);
  }, []);

  return (
    <SessionContext.Provider
      value={{
        preferences,
        setPreferences,
        photo,
        setPhoto,
        routine,
        setRoutine,
        inspiration,
        setInspiration,
        reset,
        resetEphemeralDemo,
      }}
    >
      {children}
    </SessionContext.Provider>
  );
};

export const useSession = () => {
  const ctx = useContext(SessionContext);
  if (!ctx) throw new Error("useSession must be used within SessionProvider");
  return ctx;
};
